import * as faceapi from "face-api.js";

// Global variable to track model loading
let modelsLoaded = false;

let SSDmodelsLoaded = false;

const MODEL_URL =
  "https://kbcisr.s3.ap-southeast-2.amazonaws.com/ISR2025/static/Models";

const MIN_FACE_HEIGHT_PERCENT = 0.3;
const MAX_FACE_HEIGHT_PERCENT = 1;
const CENTER_TOLERANCE_PERCENT = 0.15;
const MAX_BACKGROUND_COLOR_VARIANCE = 1200; // Adjusted for leniency
const MIN_WHITE_COLOR = 170;
const MIN_BLUE_DOMINANCE = 15;


//Test 
export const PhotoErrorMessages = {
  NoFaceDetected: "No face was detected in the photo. Try zooming in the image slightly."
}

export const loadFaceApiModels = async () => {
  if (modelsLoaded)
    return { error: false, message: "Face-API models already loaded" };
  try {
    await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
    modelsLoaded = true;
    return { error: false, message: "Face-API models loaded successfully." };
  } catch (error) {
    return { error: true, message: `Failed to load Face-API models: ${error}` };
  }
};

export const loadFaceApiSSDModel = async () => {
  if (SSDmodelsLoaded)
    return { error: false, message: "Face-API SSD model already loaded" };
  try {
    await faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);
    SSDmodelsLoaded = true;
    return { error: false, message: "Face-API SSD model loaded successfully." };
  } catch (error) {
    return {
      error: true,
      message: `Failed to load SSD Face-API model: ${error}`,
    };
  }
};

/**
 * Validates a photo against passport-like requirements using face-api.js.
 * * @param {string} dataURL The Base64 encoded image string (e.g., "data:image/jpeg;base64,...").
 * @returns {Promise<string[]>} An array of error messages, or an empty array if valid.
 */
export const validatePhotoRequirements = async (dataURL) => {
  const errors = [];
  console.log("RUNNNN");

  // Ensure constants (like modelsLoaded) are accessible here
  if (typeof modelsLoaded === "undefined" || !modelsLoaded) {
    errors.push("Face detection models are not yet loaded. Please try again.");
    return errors;
  }

  // --- 1. Load the Image ---
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = (err) =>
      reject(new Error("Failed to load image from dataURL."));
    image.src = dataURL;
  }).catch((err) => {
    errors.push(err.message);
    return null;
  });

  if (!img) return errors;

  const options = new faceapi.TinyFaceDetectorOptions();

  // --- 2. Detect Faces ---
  const detections = await faceapi.detectAllFaces(img, options);

  // --- 3. Face Count Validation ---
  if (detections.length !== 1) {
    errors.push(
      detections.length === 0
        ? "No face was detected in the photo. Try zooming in the image slightly."
        : "Multiple faces detected. The photo must contain exactly one person."
    );
    return errors;
  }

  const detection = detections[0];
  const box = detection.box;

  // Check for required constants
  if (typeof MIN_FACE_HEIGHT_PERCENT === "undefined") {
    errors.push("Configuration error: Required constants are missing.");
    return errors;
  }

  // --- 4. Face Size Validation (40% - 80% of image height) ---
  const faceHeightPercent = box.height / img.height;
  if (
    faceHeightPercent < MIN_FACE_HEIGHT_PERCENT ||
    faceHeightPercent > MAX_FACE_HEIGHT_PERCENT
  ) {
    errors.push(
      `Face needs to cover at least ${
        MIN_FACE_HEIGHT_PERCENT * 100
      }% of the image. Currently face occupies ${Math.round(
        faceHeightPercent * 100
      )}% of the image height. Try zooming in the image.`
    );
  }

  // --- 5. Face Centering Validation ---
  const faceCenterX = box.left + box.width / 2;
  const imageCenterX = img.width / 2;
  const faceCenterY = box.top + box.height / 2;
  const imageCenterY = img.height / 2;

  const centerToleranceX = img.width * CENTER_TOLERANCE_PERCENT;
  const centerToleranceY = img.height * CENTER_TOLERANCE_PERCENT;

  if (
    Math.abs(faceCenterX - imageCenterX) > centerToleranceX ||
    Math.abs(faceCenterY - imageCenterY) > centerToleranceY
  ) {
    errors.push(
      "The face is not centered enough in the image. Ensure your head is near the middle of the photo."
    );
  }

  // --- 6. ADVANCED BACKGROUND ANALYSIS (Top and Side Rectangles) ---

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0, img.width, img.height);

  // Define the three background Regions of Interest (ROIs)
  const rois = [];
  const faceBottom = box.top + box.height;

  // ROI 1: Top of image to top of face box (full width)
  const topMarginHeight = Math.max(0, box.top); // Height from y=0 to box.top
  if (topMarginHeight > 10) {
    // Ensure a minimum height for analysis
    rois.push({ x: 0, y: 0, width: img.width, height: topMarginHeight });
  } else {
    errors.push(
      "Cannot reliably check background uniformity: Head is positioned too high in the image."
    );
    // Skip analysis if top margin is too small
    return errors;
  }

  // ROI 2: Left margin (Left of face box)
  const leftMarginWidth = Math.max(0, box.left);
  if (leftMarginWidth > 10) {
    rois.push({ x: 0, y: 0, width: leftMarginWidth, height: faceBottom });
  }

  // ROI 3: Right margin (Right of face box)
  const rightMarginWidth = Math.max(0, img.width - (box.left + box.width));
  if (rightMarginWidth > 10) {
    rois.push({
      x: box.left + box.width,
      y: 0,
      width: rightMarginWidth,
      height: faceBottom,
    });
  }

  let combinedR = 0,
    combinedG = 0,
    combinedB = 0;
  let combinedPixelCount = 0;
  const allPixelData = []; // Store pixel values for variance calculation

  // Iterate over all valid ROIs, extract data, and aggregate metrics
  for (const roi of rois) {
    const roiData = ctx.getImageData(roi.x, roi.y, roi.width, roi.height).data;

    // Sum total color values and store pixel data
    for (let i = 0; i < roiData.length; i += 4) {
      const r = roiData[i],
        g = roiData[i + 1],
        b = roiData[i + 2];
      combinedR += r;
      combinedG += g;
      combinedB += b;

      // Store R, G, B for later variance calculation
      allPixelData.push(r, g, b);
    }
    combinedPixelCount += roi.width * roi.height;
  }

  // If no pixels were sampled (shouldn't happen with the box.top check)
  if (combinedPixelCount === 0) {
    errors.push(
      "Internal Error: Could not sample sufficient background pixels."
    );
    return errors;
  }

  // Calculate final average colors
  const avgR = combinedR / combinedPixelCount;
  const avgG = combinedG / combinedPixelCount;
  const avgB = combinedB / combinedPixelCount;

  /*    // Calculate variance (proxy for uniformity/clutter)
    let variance = 0;
    for (const colorValue of allPixelData) {
        // Find the average for the current channel (R, G, or B)
        const avgChannel = (allPixelData.indexOf(colorValue) % 3 === 0) ? avgR : 
                           (allPixelData.indexOf(colorValue) % 3 === 1) ? avgG : avgB;
        
        variance += (colorValue - avgChannel) ** 2;
    }
    const meanVariance = variance / allPixelData.length;

    // --- Clutter/Uniformity Check ---
    if (meanVariance > MAX_BACKGROUND_COLOR_VARIANCE) {
        errors.push(
            `Background is not uniform (clutter detected). Variance: ${Math.round(meanVariance)}. Please use a plain, solid background.`
        );
    } */

  // --- Color Check (using adjusted thresholds) ---
  // Acceptable colors: White or Blue
  const isWhite =
    avgR > MIN_WHITE_COLOR && avgG > MIN_WHITE_COLOR && avgB > MIN_WHITE_COLOR;
  const isBlue =
    avgB > avgR + MIN_BLUE_DOMINANCE && avgB > avgG + MIN_BLUE_DOMINANCE;

  if (!isWhite) {
    errors.push(
      `Background color is not acceptable. Only plain white backgrounds are allowed.`
    );
  }

  return errors;
};

export const validatePhotoRequirementsSDDModel = async (dataURL) => {
  const errors = [];
  console.log("RUNNNN");

  // Ensure constants (like modelsLoaded) are accessible here
  if (typeof modelsLoaded === "undefined" || !modelsLoaded) {
    errors.push("Face detection models are not yet loaded. Please try again.");
    return errors;
  }

  // --- 1. Load the Image ---
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = (err) =>
      reject(new Error("Failed to load image from dataURL."));
    image.src = dataURL;
  }).catch((err) => {
    errors.push(err.message);
    return null;
  });

  if (!img) return errors;

  const SSDoptions = new faceapi.SsdMobilenetv1Options({
    minConfidence: 0.7, // Higher confidence requirement = faster
    maxResults: 1, // Only need one face result
    // You typically don't need to specify input size for SSD like you did for TinyFaceDetector
  });

  // --- 2. Detect Faces ---
  const detections = await faceapi.detectAllFaces(img, SSDoptions);

  // --- 3. Face Count Validation ---
  if (detections.length !== 1) {
    errors.push(
      detections.length === 0
        ? "No face was detected in the photo. Try zooming in the image slightly."
        : "Multiple faces detected. The photo must contain exactly one person."
    );
    return errors;
  }

  const detection = detections[0];
  const box = detection.box;

  // Check for required constants
  if (typeof MIN_FACE_HEIGHT_PERCENT === "undefined") {
    errors.push("Configuration error: Required constants are missing.");
    return errors;
  }

  // --- 4. Face Size Validation (40% - 80% of image height) ---
  const faceHeightPercent = box.height / img.height;
  if (
    faceHeightPercent < MIN_FACE_HEIGHT_PERCENT ||
    faceHeightPercent > MAX_FACE_HEIGHT_PERCENT
  ) {
    errors.push(
      `Face needs to cover at least ${
        MIN_FACE_HEIGHT_PERCENT * 100
      }% of the image. Currently face occupies ${Math.round(
        faceHeightPercent * 100
      )}% of the image height. Try zooming in the image.`
    );
  }

  // --- 5. Face Centering Validation ---
  const faceCenterX = box.left + box.width / 2;
  const imageCenterX = img.width / 2;
  const faceCenterY = box.top + box.height / 2;
  const imageCenterY = img.height / 2;

  const centerToleranceX = img.width * CENTER_TOLERANCE_PERCENT;
  const centerToleranceY = img.height * CENTER_TOLERANCE_PERCENT;

  if (
    Math.abs(faceCenterX - imageCenterX) > centerToleranceX ||
    Math.abs(faceCenterY - imageCenterY) > centerToleranceY
  ) {
    errors.push(
      "The face is not centered enough in the image. Ensure your head is near the middle of the photo."
    );
  }

  // --- 6. ADVANCED BACKGROUND ANALYSIS (Top and Side Rectangles) ---

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0, img.width, img.height);

  // Define the three background Regions of Interest (ROIs)
  const rois = [];
  const faceBottom = box.top + box.height;

  // ROI 1: Top of image to top of face box (full width)
  const topMarginHeight = Math.max(0, box.top); // Height from y=0 to box.top
  if (topMarginHeight > 10) {
    // Ensure a minimum height for analysis
    rois.push({ x: 0, y: 0, width: img.width, height: topMarginHeight });
  } else {
    errors.push(
      "Cannot reliably check background uniformity: Head is positioned too high in the image."
    );
    // Skip analysis if top margin is too small
    return errors;
  }

  // ROI 2: Left margin (Left of face box)
  const leftMarginWidth = Math.max(0, box.left);
  if (leftMarginWidth > 10) {
    rois.push({ x: 0, y: 0, width: leftMarginWidth, height: faceBottom });
  }

  // ROI 3: Right margin (Right of face box)
  const rightMarginWidth = Math.max(0, img.width - (box.left + box.width));
  if (rightMarginWidth > 10) {
    rois.push({
      x: box.left + box.width,
      y: 0,
      width: rightMarginWidth,
      height: faceBottom,
    });
  }

  let combinedR = 0,
    combinedG = 0,
    combinedB = 0;
  let combinedPixelCount = 0;
  const allPixelData = []; // Store pixel values for variance calculation

  // Iterate over all valid ROIs, extract data, and aggregate metrics
  for (const roi of rois) {
    const roiData = ctx.getImageData(roi.x, roi.y, roi.width, roi.height).data;

    // Sum total color values and store pixel data
    for (let i = 0; i < roiData.length; i += 4) {
      const r = roiData[i],
        g = roiData[i + 1],
        b = roiData[i + 2];
      combinedR += r;
      combinedG += g;
      combinedB += b;

      // Store R, G, B for later variance calculation
      allPixelData.push(r, g, b);
    }
    combinedPixelCount += roi.width * roi.height;
  }

  // If no pixels were sampled (shouldn't happen with the box.top check)
  if (combinedPixelCount === 0) {
    errors.push(
      "Internal Error: Could not sample sufficient background pixels."
    );
    return errors;
  }

  // Calculate final average colors
  const avgR = combinedR / combinedPixelCount;
  const avgG = combinedG / combinedPixelCount;
  const avgB = combinedB / combinedPixelCount;

  /*    // Calculate variance (proxy for uniformity/clutter)
    let variance = 0;
    for (const colorValue of allPixelData) {
        // Find the average for the current channel (R, G, or B)
        const avgChannel = (allPixelData.indexOf(colorValue) % 3 === 0) ? avgR : 
                           (allPixelData.indexOf(colorValue) % 3 === 1) ? avgG : avgB;
        
        variance += (colorValue - avgChannel) ** 2;
    }
    const meanVariance = variance / allPixelData.length;

    // --- Clutter/Uniformity Check ---
    if (meanVariance > MAX_BACKGROUND_COLOR_VARIANCE) {
        errors.push(
            `Background is not uniform (clutter detected). Variance: ${Math.round(meanVariance)}. Please use a plain, solid background.`
        );
    } */

  // --- Color Check (using adjusted thresholds) ---
  // Acceptable colors: White or Blue
  const isWhite =
    avgR > MIN_WHITE_COLOR && avgG > MIN_WHITE_COLOR && avgB > MIN_WHITE_COLOR;
  const isBlue =
    avgB > avgR + MIN_BLUE_DOMINANCE && avgB > avgG + MIN_BLUE_DOMINANCE;

  if (!isWhite) {
    errors.push(
      `Background color is not acceptable. Only plain white backgrounds are allowed.`
    );
  }

  return errors;
};
